import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { dispatcherFactory, selectorHookFactory } from '~/store/utils'

export type SortDir = 'asc' | 'desc'
export type SortKey =
  | 'depotNm'
  | 'utilization'
  | 'powerCeiling'
  | 'connectorsInUse'
  | 'totalChargers'
  | 'isLoadBalanced'
  | 'siteStatus'

type SiteOverviewSlice = {
  orgIds: string[]
  siteIds: string[]
  searchString: string
  selectedId?: string
  sortBy: {
    key: SortKey
    dir: SortDir
  }
  commissioningStates: string[]
}

const initialState: SiteOverviewSlice = {
  orgIds: [],
  siteIds: [],
  searchString: '',
  sortBy: {
    key: 'depotNm',
    dir: 'asc'
  },
  commissioningStates: ['COMMISSIONED']
}

const siteOverviewSlice = createSlice({
  name: 'siteOverview',
  initialState,
  reducers: {
    reset: () => initialState,
    setOrgIds: (state, action: PayloadAction<string[]>) => ({ ...state, orgIds: action.payload }),
    setSiteIds: (state, action: PayloadAction<string[]>) => ({ ...state, siteIds: action.payload }),
    setSearchString: (state, action: PayloadAction<string>) => ({ ...state, searchString: action.payload }),

    // Passed in as a string for convenience. Key and direction should be separated by a colon.
    setSortBy: (state, action: PayloadAction<string>) => {
      const [key, dir] = action.payload.split(':') as [SortKey, SortDir]
      return { ...state, sortBy: { key, dir } }
    },

    toggleSelectedId: (state, action: PayloadAction<string>) => {
      const selectedId = state.selectedId === action.payload ? undefined : action.payload
      return { ...state, selectedId }
    },

    setCommissioningStates: (state, action: PayloadAction<string[]>) => ({
      ...state,
      commissioningStates: action.payload
    })
  }
})

export default siteOverviewSlice.reducer

export const useSelectedOrgIds = selectorHookFactory((state) => state.siteOverview.orgIds)
export const useSelectedSiteIds = selectorHookFactory((state) => state.siteOverview.siteIds)
export const useSearchString = selectorHookFactory((state) => state.siteOverview.searchString)
export const useSelectedId = selectorHookFactory((state) => state.siteOverview.selectedId)
export const useSortBy = selectorHookFactory((state) => state.siteOverview.sortBy)
export const useFilterCount = selectorHookFactory(
  (state) => state.siteOverview.orgIds.length + state.siteOverview.siteIds.length
)
export const useSelectedCommissioningStates = selectorHookFactory((state) => state.siteOverview.commissioningStates)

const { reset, setOrgIds, setSiteIds, setSearchString, toggleSelectedId, setSortBy, setCommissioningStates } =
  siteOverviewSlice.actions
export { reset }

export const useUpdateOrgIds = dispatcherFactory(setOrgIds)
export const useUpdateSiteIds = dispatcherFactory(setSiteIds)
export const useUpdateSearchString = dispatcherFactory(setSearchString)
export const useToggleSelectedId = dispatcherFactory(toggleSelectedId)
export const useUpdateSortBy = dispatcherFactory(setSortBy)
export const useUpdateCommissioningStates = dispatcherFactory(setCommissioningStates)
